import { useCallback, useMemo, useState } from "react";
import { api } from "../api.js";
import { useResource } from "../useResource.js";
import { Empty, Failed, Loading } from "./Loading.js";
import styles from "./Triage.module.css";

/**
 * The triage queue: songs the app thinks are the same recording.
 *
 * Built to be abandoned. Every row arrives already decided — certain kinds linked, open questions
 * left separate — so stopping after four rows leaves the library exactly as valid as finishing.
 * Progress is stated, decisions persist one at a time, and nothing here gates rating.
 *
 * The shape §J7 ruled for the 206 unmapped tags: sorted by what it costs to leave alone, bulk
 * answers available, never a modal per row.
 */

export interface TriageMember {
  songId: number;
  title: string;
  albumId: number;
  albumTitle: string;
  albumSize: number;
  albumYear: number | null;
  rating: number | null;
  isDefinitive: boolean;
}

export interface TriageGroup {
  artistId: number;
  signature: string;
  artist: string;
  kind: string;
  state: string;
  resolved: boolean;
  definitiveSongId: number | null;
  conflictingRatings: number[];
  coinToss: boolean;
  members: TriageMember[];
}

interface Payload {
  total: number;
  totalGroups: number;
  resolved: number;
  attention: number;
  groups: TriageGroup[];
}

/** Why the app is asking, in one line. */
const WHY: Record<string, string> = {
  performance: "A DIFFERENT PERFORMANCE — LIVE, ACOUSTIC OR A DEMO",
  rerecording: "A RE-RECORDING, MADE YEARS LATER ON PURPOSE",
  unknown: "THE APP COULD NOT TELL",
  identical: "THE SAME TITLE, ON TWO ALBUMS",
  punctuation: "THE SAME TITLE, TYPED DIFFERENTLY",
  variant: "A REMASTER OR AN ALTERNATE MIX",
};

const PAGE = 25;

export function Triage() {
  const [offset, setOffset] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const queue = useResource(
    () => api.get<Payload>(`/api/triage?needsAttention=${!showAll}&limit=${PAGE}&offset=${offset}`),
    [showAll, offset],
  );

  const decide = useCallback(
    async (group: TriageGroup, same: boolean, definitiveSongId?: number) => {
      setBusy(true);
      try {
        await api.post("/api/triage/decide", {
          artistId: group.artistId,
          signature: group.signature,
          same,
          definitiveSongId,
        });
        queue.reload();
      } finally {
        setBusy(false);
      }
    },
    [queue],
  );

  /**
   * The bulk lever. On the measured library 16 of 27 questions come from three albums — seven
   * from Live in Buenos Aires, six from MTV Unplugged. That is three decisions, not sixteen.
   */
  const byAlbum = useMemo(() => {
    const map = new Map<string, TriageGroup[]>();
    for (const group of queue.data?.groups ?? []) {
      // The album that is NOT definitive is the one the question is really about.
      const other = group.members.find((m) => !m.isDefinitive) ?? group.members[0]!;
      const list = map.get(other.albumTitle) ?? [];
      list.push(group);
      map.set(other.albumTitle, list);
    }
    return [...map.entries()].filter(([, groups]) => groups.length > 1).sort((a, b) => b[1].length - a[1].length);
  }, [queue.data]);

  const bulk = useCallback(
    async (groups: TriageGroup[], same: boolean) => {
      setBusy(true);
      try {
        await api.post("/api/triage/bulk", {
          decisions: groups.map((g) => ({ artistId: g.artistId, signature: g.signature, same })),
        });
        queue.reload();
      } finally {
        setBusy(false);
      }
    },
    [queue],
  );

  if (queue.error) return <Failed error={queue.error} retry={queue.reload} />;
  if (queue.loading && !queue.data) return <Loading what="the queue" />;

  const data = queue.data!;

  return (
    <div className={styles.page} data-testid="triage">
      <div className={styles.title}>Same song?</div>

      <div className={styles.lede}>
        These look like one recording appearing on more than one album. Linking them means one
        rating shared across every album it appears on. <b>Nothing here blocks rating</b> — answer
        a few and come back.
      </div>

      <div className={styles.filters}>
        <span className={styles.progress} data-testid="triage-progress">
          {data.resolved.toLocaleString()} OF {data.totalGroups.toLocaleString()} DECIDED ·{" "}
          <b>{data.attention.toLocaleString()} STILL NEED YOU</b>
        </span>
        <button
          type="button"
          className={styles.filter}
          data-active={showAll}
          data-testid="show-all"
          onClick={() => {
            setShowAll((v) => !v);
            setOffset(0);
          }}
        >
          SHOW THE ONES THE APP SETTLED
        </button>
      </div>

      {byAlbum.length > 0 && (
        <div className={styles.bulk} data-testid="bulk">
          <div className={styles.bulkLead}>ANSWER A WHOLE ALBUM AT ONCE</div>
          {byAlbum.map(([album, groups]) => (
            <div key={album} className={styles.bulkRow}>
              <span className={styles.bulkAlbum}>{album}</span>
              <span className={styles.bulkCount}>{groups.length} QUESTIONS</span>
              <button type="button" className={styles.bulkAction} disabled={busy} onClick={() => void bulk(groups, false)}>
                All different songs
              </button>
              <button type="button" className={styles.bulkAction} disabled={busy} onClick={() => void bulk(groups, true)}>
                All the same
              </button>
            </div>
          ))}
        </div>
      )}

      {data.groups.length === 0 ? (
        <Empty
          title={showAll ? "Nothing to decide" : "Nothing needs you"}
          hint={showAll ? undefined : "EVERY QUESTION THE APP COULD NOT ANSWER IS DONE"}
        />
      ) : (
        <div className={styles.groups}>
          {data.groups.map((group) => (
            <div key={`${group.artistId}|${group.signature}`} className={styles.group} data-testid="triage-group">
              <div className={styles.head}>
                <span className={styles.artist}>{group.artist}</span>
                <span className={styles.why} data-kind={group.kind}>
                  {WHY[group.kind] ?? group.kind.toUpperCase()}
                </span>
                {group.conflictingRatings.length > 0 && (
                  <span className={styles.warn} data-testid="rating-conflict">
                    YOU RATED THESE {group.conflictingRatings.join(" AND ")} — LINKING KEEPS THE HIGHER
                  </span>
                )}
              </div>

              <div className={styles.members}>
                {group.members.map((member) => (
                  <button
                    key={member.songId}
                    type="button"
                    className={styles.member}
                    data-definitive={member.isDefinitive || undefined}
                    data-testid="triage-member"
                    disabled={busy}
                    // Clicking a member says "this is the one that holds the rating" AND links.
                    onClick={() => void decide(group, true, member.songId)}
                  >
                    <span className={styles.memberTitle}>{member.title}</span>
                    <span className={styles.memberAlbum}>
                      {member.albumTitle}
                      {member.albumYear !== null && <span className={styles.memberYear}> {member.albumYear}</span>}
                    </span>
                    <span className={styles.memberHeld}>{member.albumSize} TRACKS HELD</span>
                    <span className={styles.memberRating}>{member.rating ?? "—"}</span>
                    {member.isDefinitive && <span className={styles.definitive}>DEFINITIVE</span>}
                  </button>
                ))}
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={busy}
                  data-testid="decide-same"
                  onClick={() => void decide(group, true, group.definitiveSongId ?? undefined)}
                >
                  Same song
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  data-testid="decide-different"
                  onClick={() => void decide(group, false)}
                >
                  Different songs
                </button>
                <span className={styles.hint}>OR PICK WHICH ALBUM IT BELONGS TO ABOVE</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {(offset > 0 || data.total > offset + PAGE) && (
        <div className={styles.pager}>
          <button type="button" className={styles.secondary} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            ← Back
          </button>
          <span className={styles.pagerCount}>
            {offset + 1}–{Math.min(offset + PAGE, data.total)} OF {data.total.toLocaleString()}
          </span>
          <button
            type="button"
            className={styles.secondary}
            disabled={offset + PAGE >= data.total}
            data-testid="triage-next"
            onClick={() => setOffset(offset + PAGE)}
          >
            More →
          </button>
        </div>
      )}
    </div>
  );
}
